"use client";

import { useState } from "react";
import { useCategoryStore } from "@/stores/categoryStore";
import { showToast } from "@/lib/toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface ModalDeleteCategoryProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string | null;
  onComplete: () => void;
}

export default function ModalDeleteCategory({
  isOpen,
  onClose,
  categoryId,
  onComplete,
}: ModalDeleteCategoryProps) {
  const { deleteCategory, isDeleting } = useCategoryStore();
  const [loading, setLoading] = useState(false);

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) return;

    try {
      setLoading(true);
      const ok = await deleteCategory(categoryId);
      if (ok) {
        showToast.success("Thành công", "Xóa danh mục thành công");
        onComplete();
        onClose();
      } else {
        showToast.error("Lỗi", "Không thể xóa danh mục");
      }
    } catch (e) {
      showToast.error("Lỗi", "Có lỗi xảy ra khi xóa danh mục");
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || isDeleting;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white uppercase">
            Xác nhận xóa
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-gray-700 dark:text-gray-300">
                Bạn có chắc chắn muốn xóa danh mục này?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Hành động này không thể hoàn tác.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Hủy
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? "Đang xóa..." : "Xác nhận xóa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
